import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CafCorpusEntry, CafGuidanceVersion, listCafGuidanceHistory } from '../../../api/cafCorpus';
import { formatCount } from '../format';
import { ActionPill, useClaimFieldOptions } from '../LabelForm';
import { senderEmail } from '../lenders';
import { Drawer } from '../Overlay';
import { describeHistory } from './historyDiff';

const PAGE = 100;
const MAX_PAGES = 20;

export const HistoryDrawer: React.FC<{
  /** Only id/sender/correct_action are read here -- Task 23's decision drawer
   *  opens this from a precedent's evidence, not a full corpus entry, so
   *  callers may pass a plain object instead of casting one up. */
  entry: Pick<CafCorpusEntry, 'id' | 'sender' | 'correct_action'>;
  lenderOf: (sender: string) => string | null;
  onClose: () => void;
}> = ({ entry, lenderOf, onClose }) => {
  const claimFields = useClaimFieldOptions();
  const [versions, setVersions] = useState<CafGuidanceVersion[] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const [problem, setProblem] = useState('');
  // The MAX_PAGES cut-off: information about what's shown, never an error.
  const [limitNote, setLimitNote] = useState('');
  // A LATER page failing keeps whatever pages already loaded on screen (old CafGuidanceHistory's
  // behaviour) instead of throwing the whole history away -- pageProblem carries the retry.
  const [pageProblem, setPageProblem] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const generation = useRef(0);

  const loadFrom = useCallback(async (startCursor: string | undefined, startPages: number, loaded: CafGuidanceVersion[]) => {
    const mine = generation.current;
    let all = loaded;
    let next = startCursor;
    let pages = startPages;
    setPageProblem(false);
    while (pages < MAX_PAGES) {
      let page;
      try {
        // eslint-disable-next-line no-await-in-loop
        page = await listCafGuidanceHistory(entry.id, entry.sender, next, PAGE);
      } catch {
        if (mine !== generation.current) return;
        if (!all.length) {
          setProblem("Couldn't load the history. Close and try again.");
        } else {
          setVersions(all);
          setCursor(next);
          setPagesLoaded(pages);
          setPageProblem(true);
        }
        return;
      }
      if (mine !== generation.current) return;
      all = [...all, ...page.items];
      pages += 1;
      if (!page.next_cursor) {
        next = undefined;
        break;
      }
      next = page.next_cursor;
    }
    setVersions(all);
    setCursor(next);
    setPagesLoaded(pages);
    // The MAX_PAGES cut-off (unlike a page failing) isn't a retryable problem -- just say what's shown.
    setLimitNote(next ? `Showing the first ${formatCount(all.length)} changes.` : '');
  }, [entry.id, entry.sender]);

  useEffect(() => {
    generation.current += 1;
    setVersions(null);
    setProblem('');
    setLimitNote('');
    setPageProblem(false);
    setCursor(undefined);
    setPagesLoaded(0);
    void loadFrom(undefined, 0, []);
  }, [entry.id, entry.sender, loadFrom]);

  const retry = () => {
    setRetrying(true);
    void loadFrom(cursor, pagesLoaded, versions ?? []).finally(() => setRetrying(false));
  };

  // Pages load oldest first, so the loaded set is only complete -- and its last item truly
  // "Current" -- once pagination ran out on its own (no failure, no MAX_PAGES cut-off).
  const complete = cursor === undefined;
  const items = versions ? describeHistory(versions, claimFields, complete) : [];
  return (
    <Drawer eyebrow="History" title={lenderOf(entry.sender) ?? senderEmail(entry.sender)}
      subtitle={<>{senderEmail(entry.sender)} · <ActionPill action={entry.correct_action} /></>} onClose={onClose}
      footer={<button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose}>Close</button>}>
      <p className="cur-hint">Every change to this example, newest first. Past decisions keep the version they used.</p>
      {problem && <p className="cur-hint" role="alert">{problem}</p>}
      {limitNote && <p className="cur-hint" role="status">{limitNote}</p>}
      {!versions && !problem && <p className="cur-hint">Loading…</p>}
      {versions && !items.length && !pageProblem && <p className="cur-hint">No history has been recorded for this example yet.</p>}
      {items.length > 0 && (
        <ol className="cur-timeline" aria-label="Changes, newest first">
          {items.map((item) => (
            <li key={item.key} className={`cur-tl${item.off ? ' is-off' : ''}`}>
              <span className="cur-tl-dot" aria-hidden="true" />
              <div className="cur-tl-head">
                <strong>{item.title}</strong>
                <span className="cur-muted">{item.versionLabel} · {item.when} · {item.who}</span>
                {item.current && <span className="cur-pill cur-pill--teal">Current</span>}
              </div>
              {item.reason && <div className="cur-tl-reason"><span className="cur-muted">Reason:</span> {item.reason}</div>}
              {(item.rows.length > 0 || item.changes.length > 0) && (
                <div className="cur-chg">
                  {item.rows.map((row) => (
                    <div key={row.field} className="cur-chg-row"><div className="cur-f">{row.field}</div><div>{row.value}</div></div>
                  ))}
                  {item.changes.map((change) => (
                    <div key={change.field} className="cur-chg-row">
                      <div className="cur-f">{change.field}</div>
                      <div><span className="cur-was">{change.was}</span><span className="cur-now">{change.now}</span></div>
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {pageProblem && (
        <div className="cur-alert cur-alert--error" role="alert">
          <span className="cur-grow">Couldn&apos;t load the newer changes. Try again.</span>
          <button type="button" className="ui-btn ui-btn--danger ui-btn--sm" disabled={retrying} onClick={retry}>
            {retrying ? 'Trying…' : 'Try again'}
          </button>
        </div>
      )}
    </Drawer>
  );
};
